import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ProductManagement from './ProductManagement';
import OrderManagement from './OrderManagement';
import UserManagement from './UserManagement';
import LoadingSpinner from '../common/LoadingSpinner';
import api from '../../utils/api';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('orders');
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalProducts: 0,
    pendingOrders: 0,
    totalRevenue: 0,
    totalUsers: 0,
    adminUsers: 0,
  });
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [ordersRes, productsRes, usersRes] = await Promise.all([
        api.get('/orders'),
        api.get('/products'),
        api.get('/users').catch(() => ({ data: [] })),
      ]);

      const orders = ordersRes.data || [];
      const products = productsRes.data || [];
      const users = usersRes.data || [];

      setStats({
        totalOrders: orders.length,
        totalProducts: products.length,
        pendingOrders: orders.filter((o) => o.status === 'pending').length,
        totalRevenue: orders.reduce((acc, o) => acc + (o.total || 0), 0),
        totalUsers: users.length,
        adminUsers: users.filter((u) => u.role === 'admin').length,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <span className="px-3 py-1 bg-purple-100 text-purple-800 text-xs font-semibold rounded-full border border-purple-200">
                Admin Control Panel
              </span>
            </div>
            <p className="text-gray-500 mt-1">Welcome back, <span className="font-semibold text-gray-700">{user?.name}</span> ({user?.email})</p>
          </div>
          <Link to="/" className="btn-secondary text-sm flex items-center gap-2">
            View Public Storefront →
          </Link>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Orders</h3>
            <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalOrders}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Pending Orders</h3>
            <p className="text-3xl font-bold text-amber-600 mt-2">{stats.pendingOrders}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Products</h3>
            <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalProducts}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Revenue</h3>
            <p className="text-3xl font-bold text-emerald-600 mt-2">
              ${stats.totalRevenue.toFixed(2)}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Users & Admins</h3>
            <p className="text-3xl font-bold text-indigo-600 mt-2">
              {stats.totalUsers} <span className="text-xs font-normal text-gray-500">({stats.adminUsers} Admins)</span>
            </p>
          </div>
        </div>

        {/* Management Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="border-b bg-gray-50/50">
            <nav className="flex overflow-x-auto">
              <button
                onClick={() => setActiveTab('orders')}
                className={`px-6 py-4 font-semibold text-sm transition-all border-b-2 whitespace-nowrap ${
                  activeTab === 'orders'
                    ? 'text-primary border-primary bg-white'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
              >
                📦 Orders Management
              </button>
              <button
                onClick={() => setActiveTab('products')}
                className={`px-6 py-4 font-semibold text-sm transition-all border-b-2 whitespace-nowrap ${
                  activeTab === 'products'
                    ? 'text-primary border-primary bg-white'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
              >
                🏷️ Products Inventory
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className={`px-6 py-4 font-semibold text-sm transition-all border-b-2 whitespace-nowrap ${
                  activeTab === 'users'
                    ? 'text-primary border-primary bg-white'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
              >
                👥 User Role Management
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'orders' && <OrderManagement />}
            {activeTab === 'products' && <ProductManagement />}
            {activeTab === 'users' && <UserManagement />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;